<template>
  <v-btn
    :icon="mdiInformationSlabCircleOutline"
    :color="btnColor"
    @click="dialog = true"
  />

  <v-dialog
    v-model="dialog"
    width="auto"
    min-width="30%"
  >
    <v-card>
      <v-card-title>
        {{ $t('global.info-modal.title') }}
      </v-card-title>

      <v-divider />

      <v-card-text>
        <v-img
            height="150"
            src="/assets/logo.png"
        />
        <v-container>
          <div class="text-subtitle-1 mb-5">
            <span class="font-weight-bold">{{ $t('global.info-modal.label.last-sync') }}: </span>{{ lastSyncDateForHuman }}

            <template v-if="applicationStore.syncStatus.completeCycle">...</template>
            <template v-if="applicationStore.syncStatus.partialCycle">(...)</template>
          </div>

          <div class="text-subtitle-1 mb-2">
            <span class="font-weight-bold">{{ $t('global.info-modal.label.application-version') }}: </span>{{ buildDate }}
          </div>

          <div class="text-subtitle-1 mb-2">
            <span class="font-weight-bold">{{downloadedProductImages?.length}}/{{productImages?.length}}</span> {{ $t('global.info-modal.label.downloaded-prod-images') }}
          </div>
          <v-expansion-panels>
            <v-expansion-panel
                class="mt-2"
                title="Debug"
            >
              <v-expansion-panel-text>
                <div class="text-subtitle-1" style="display: flex; align-items: center;">
                  <p class="font-weight-bold">{{ $t('global.info-modal.label.show-hidden-questions') }}: </p>
                  <v-switch
                      v-model="showHiddenQuestions"
                      color="success"
                      hide-details
                      inset
                      class="mx-2"
                      @update:modelValue="changeShowHiddenQuestionsStatus"
                  />
                </div>

                <div class="text-subtitle-1" style="display: flex; align-items: center;">
                  <p class="font-weight-bold">{{ $t('global.info-modal.label.show-debug-mode') }}: </p>
                  <v-switch
                      v-model="showDebugMode"
                      color="success"
                      hide-details
                      inset
                      class="mx-2"
                      @update:modelValue="changeShowDebugModeStatus"
                  />
                </div>

                <div class="text-subtitle-1" style="display: flex; align-items: center;">
                  <p class="font-weight-bold">{{ $t('global.info-modal.label.save-logs') }}: </p>
                  <v-switch
                      v-model="saveLogs"
                      color="success"
                      hide-details
                      inset
                      class="mx-2"
                      @update:modelValue="changeSaveLogsStatus"
                  />
                  <v-btn
                      variant="text"
                      :icon="mdiBugOutline"
                      @click="goToLogs"
                  ></v-btn>
                </div>

                <v-btn
                    :disabled="!isConnectedAndOnline"
                    class="my-2"
                    variant="tonal"
                    :prepend-icon="mdiDatabaseArrowRightOutline"
                    :loading="sendingDB"
                    @click="sendDB"
                >
                  {{ $t('global.info-modal.label.send-db') }}
                </v-btn>
                <br>
                <load-d-b-modal :is-connected-and-online="isConnectedAndOnline"/>
                <br>
                <v-btn
                    :disabled="!isConnectedAndOnline"
                    class="my-2"
                    variant="tonal"
                    :prepend-icon="mdiImageMultipleOutline"
                    :loading="sendingPhotos"
                    @click="sendPhotos"
                >
                  {{ $t('global.info-modal.label.send-photos') }}
                </v-btn>
                <div v-if="photosProgress" class="text-caption ml-2">{{ photosProgress }}</div>
                <br>
                <load-photos-modal :is-connected-and-online="isConnectedAndOnline"/>
              </v-expansion-panel-text>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-container>
      </v-card-text>

      <v-divider />

      <v-card-actions>
        <v-btn
            :disabled="!isConnectedAndOnline"
            v-bind="useButtonStyle"
            :loading="currentCycle"
            :prepend-icon="mdiSync"
            @click="processSync"
        >
          {{ $t('global.info-modal.label.synchro') }}
        </v-btn>
        <v-btn
            :disabled="!isConnectedAndOnline"
            v-bind="useButtonStyle"
            :loading="currentCycle"
            :prepend-icon="mdiContentSaveAll"
            @click="processSave"
        >
          {{ $t('global.info-modal.label.save') }}
        </v-btn>
        <v-btn
            v-if="hasLogs"
            v-bind="useButtonStyle"
            :loading="sendingLogs"
            :prepend-icon="mdiInvoiceTextSendOutline"
            @click="sendLogs"
        >
          {{ $t('global.info-modal.label.send-logs') }}
        </v-btn>

        <v-icon
            v-if="logSentSuccessfully"
            :icon="mdiCheck"
            color="success"></v-icon>
        <v-spacer />
        <v-btn
          class="float-right"
          color="grey-darken-2"
          :prepend-icon="mdiClose"
          variant="tonal"
          @click="dialog = false"
        >
          {{ $t('global.btn.close') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script setup>
import {
  mdiClose,
  mdiSync,
  mdiInformationSlabCircleOutline,
  mdiBugOutline,
  mdiInvoiceTextSendOutline,
  mdiCheck,
  mdiContentSaveAll,
  mdiDatabaseArrowRightOutline,
  mdiImageMultipleOutline
} from '@mdi/js';
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useApplicationStore } from '@/stores/applicationStore';
import dateHandler from '@/utils/dateHandler';
import { useRouter } from 'vue-router';
import syncManager from "@/utils/sync/SyncManager";
import { useLogStore } from "@/stores/logStore";
import { liveQuery } from "dexie";
import { db } from '@/db.js';
import { useObservable }  from '@vueuse/rxjs';
import json from '@/buildinfo.json';
import {useButtonStyle} from "@/utils/appStyleHandler";
import {useStoreManager} from "../../stores/storeManager";
import LoadDBModal from "./LoadDBModal.vue";
import LoadPhotosModal from "./LoadPhotosModal.vue";
import frontPicturesService from "@/services/frontPictures";
import localForageService from "@/services/localForage";
import { TABLE } from "@/constants";

const props = defineProps({
  btnColor: {
    required: true
  },
  isOnline: {
    type: Boolean
  }
});

const { t } = useI18n();
const router = useRouter();
const applicationStore = useApplicationStore();
const logStore = useLogStore();
const buildDate = dateHandler.convertUnixTimestampToDateTime(json?.buildDateMs/1000)

const dialog = ref(false);
const sendingLogs = ref(false);
const logSentSuccessfully = ref(false);
const sendingDB = ref(false);
const sendingPhotos = ref(false);
const photosProgress = ref('');

const showHiddenQuestions = ref(applicationStore.showHiddenQuestions);
const showDebugMode = ref(applicationStore.showDebugMode);
const saveLogs = ref(applicationStore.saveLogs);
const userDetails = computed(() => applicationStore.userDetails || false);
const airplaneMode = computed(() => applicationStore.airplaneMode || false);
const productImages = useObservable( liveQuery (
        () => db.offlineImage.toArray()),
    {
      onError: (err) => {
        console.log(err.message)
      },
    });
const downloadedProductImages = useObservable( liveQuery (
    () => db.offlineImage
        .where({
          'downloaded': 1,
          'downloading': 0
        }).toArray()),
    {
      onError: (err) => {
        console.log(err.message)
      },
    });


const processSync = function(){
  syncManager.runCompleteCycle();
}

const processSave = function(){
  syncManager.pushAll();
}

const currentCycle = computed(() => {
  return applicationStore.syncStatus.completeCycle || applicationStore.syncStatus.partialCycle
})

const lastSyncDateForHuman = computed(() => {
  const date = applicationStore.lastSyncDate || null;
  return date ? dateHandler.convertUnixTimestampToStandardHuman(date) : t('global.text.none.m');
});

const hasLogs = computed(() => {
  return logStore?.logs?.[userDetails.value.id]?.length > 0;
});

const changeShowHiddenQuestionsStatus = (status) => {
  applicationStore.updateShowHiddenQuestions(status);
}

const changeShowDebugModeStatus = (status) => {
  applicationStore.updateShowDebugMode(status);
}

const changeSaveLogsStatus = (status) => {
  applicationStore.updateSaveLogs(status);
}
const goToLogs = () => {
  router.push({ name: 'logs' });
  dialog.value = false;
}

const sendLogs = async () => {
  sendingLogs.value = true;
  const response = await logStore.sendLogs();
  if (!response?.success) {
    console.error('Error sending logs', response?.data?.message || 'Unknown error');
  }
  else {
    logSentSuccessfully.value = true;
    logStore.clearLogs();
    setTimeout(() => {
      logSentSuccessfully.value = false;
    }, 3000);
  }
  sendingLogs.value = false;
}
const isConnectedAndOnline = computed(() => {
  return !airplaneMode.value && props.isOnline;
});

const sendDB = async () => {
  sendingDB.value = true;
  const rawStoresData = await useStoreManager().collectStoresData()
  useStoreManager().sendDB(rawStoresData);
  // to avoid multiple sending
  setTimeout(() => {
    sendingDB.value = false;
  }, 2000);
}

const sendPhotos = async () => {
  sendingPhotos.value = true;
  photosProgress.value = '';

  try {
    // Retrieve all picture keys from localForage
    await localForageService.createInstance({ name: TABLE.QUESTION_PICTURES });
    const keys = await localForageService.instance.keys();

    if (!keys || keys.length === 0) {
      photosProgress.value = t('global.info-modal.label.no-photos-to-send');
      sendingPhotos.value = false;
      return;
    }

    // Initialize a new set on the backend
    await frontPicturesService.initSet(keys.length);

    let uploaded = 0;
    for (let i = 0; i < keys.length; i++) {
      photosProgress.value = `${i + 1} / ${keys.length}`;
      const blob = await localForageService.instance.getItem(keys[i]);

      if (!blob) continue;

      let success = false;
      // First attempt
      try {
        await frontPicturesService.uploadPhoto(blob, keys[i]);
        success = true;
      } catch (e) {
        // Retry once
        try {
          await frontPicturesService.uploadPhoto(blob, keys[i]);
          success = true;
        } catch (e2) {
          console.error(`Failed to upload photo ${keys[i]} after retry`, e2);
        }
      }

      if (success) uploaded++;
    }

    photosProgress.value = `${uploaded}/${keys.length} ${t('global.info-modal.label.photos-sent-success')}`;
  } catch (e) {
    console.error('Error sending photos', e);
    photosProgress.value = '';
  }

  setTimeout(() => {
    sendingPhotos.value = false;
  }, 2000);
}

</script>